import { SourceSyncOperationStatus } from "@gx/ts-contracts/services/database/models/public_pb";

import {
  AddFilesAction,
  ClearFilesAction,
  CreateSourceFileUploadAction,
  CreateSourceFileUploadActionType,
  InitFileProgressAction,
  RemoveFileAction,
  SetErrorAction,
  SetOverallProgressAction,
  SetPhaseAction,
  SetSessionAction,
  SetShouldShowErrorsAction,
  SetSourceNameAction,
  UpdateFileProgressAction,
} from "@/pages/sources/components/create/sources/file-upload/actions";
import {
  CreateSourceFileUploadPhase,
  CreateSourceFileUploadState,
} from "@/pages/sources/components/create/sources/file-upload/types";
import {
  filterDuplicateFiles,
  isFileUploadInProgress,
} from "@/pages/sources/components/create/sources/file-upload/utils";

function setSourceName(
  state: CreateSourceFileUploadState,
  action: SetSourceNameAction,
): CreateSourceFileUploadState {
  return {
    ...state,
    sourceName: action.payload,
  };
}

function addFiles(
  state: CreateSourceFileUploadState,
  action: AddFilesAction,
): CreateSourceFileUploadState {
  return {
    ...state,
    files: [...state.files, ...filterDuplicateFiles(action.payload, state.files)],
  };
}

function removeFile(
  state: CreateSourceFileUploadState,
  action: RemoveFileAction,
): CreateSourceFileUploadState {
  return {
    ...state,
    files: state.files.filter((_, i) => i !== action.payload),
  };
}

function clearFiles(
  state: CreateSourceFileUploadState,
  _action: ClearFilesAction,
): CreateSourceFileUploadState {
  return {
    ...state,
    files: [],
    phase: CreateSourceFileUploadPhase.IDLE,
    session: null,
    fileProgressMap: new Map(),
    overallProgress: 0,
    error: null,
  };
}

function setPhase(
  state: CreateSourceFileUploadState,
  action: SetPhaseAction,
): CreateSourceFileUploadState {
  return {
    ...state,
    phase: action.payload,
    error: action.payload === CreateSourceFileUploadPhase.ERROR ? state.error : null,
  };
}

function setSession(
  state: CreateSourceFileUploadState,
  action: SetSessionAction,
): CreateSourceFileUploadState {
  return {
    ...state,
    session: action.payload,
  };
}

function setError(
  state: CreateSourceFileUploadState,
  action: SetErrorAction,
): CreateSourceFileUploadState {
  return {
    ...state,
    error: action.payload,
    phase: action.payload ? CreateSourceFileUploadPhase.ERROR : state.phase,
  };
}

function initFileProgress(
  state: CreateSourceFileUploadState,
  action: InitFileProgressAction,
): CreateSourceFileUploadState {
  return {
    ...state,
    fileProgressMap: action.payload,
  };
}

function updateFileProgress(
  state: CreateSourceFileUploadState,
  action: UpdateFileProgressAction,
): CreateSourceFileUploadState {
  const { fileId, updates } = action.payload;
  const newProgress = new Map(state.fileProgressMap);
  const existing = newProgress.get(fileId);

  if (existing) {
    newProgress.set(fileId, { ...existing, ...updates });
  }

  // Calculate overall progress based on completed files
  const totalFiles = newProgress.size;
  let completedFiles = 0;
  newProgress.forEach((fp) => {
    if (fp.status === SourceSyncOperationStatus.COMPLETED) {
      completedFiles++;
    }
  });

  return {
    ...state,
    fileProgressMap: newProgress,
    overallProgress: totalFiles > 0 ? Math.round((completedFiles / totalFiles) * 100) : 0,
  };
}

function setOverallProgress(
  state: CreateSourceFileUploadState,
  action: SetOverallProgressAction,
): CreateSourceFileUploadState {
  return {
    ...state,
    overallProgress: action.payload,
  };
}

function setShouldShowErrors(
  state: CreateSourceFileUploadState,
  action: SetShouldShowErrorsAction,
): CreateSourceFileUploadState {
  return {
    ...state,
    shouldShowErrors: action.payload,
  };
}

const createSourceFileUploadReducer = (
  state: CreateSourceFileUploadState,
  action: CreateSourceFileUploadAction,
): CreateSourceFileUploadState => {
  let newState: CreateSourceFileUploadState;

  switch (action.type) {
    case CreateSourceFileUploadActionType.SET_SOURCE_NAME:
      newState = setSourceName(state, action);
      break;
    case CreateSourceFileUploadActionType.ADD_FILES:
      newState = addFiles(state, action);
      break;
    case CreateSourceFileUploadActionType.REMOVE_FILE:
      newState = removeFile(state, action);
      break;
    case CreateSourceFileUploadActionType.CLEAR_FILES:
      newState = clearFiles(state, action);
      break;
    case CreateSourceFileUploadActionType.SET_PHASE:
      newState = setPhase(state, action);
      break;
    case CreateSourceFileUploadActionType.SET_SESSION:
      newState = setSession(state, action);
      break;
    case CreateSourceFileUploadActionType.SET_ERROR:
      newState = setError(state, action);
      break;
    case CreateSourceFileUploadActionType.INIT_FILE_PROGRESS:
      newState = initFileProgress(state, action);
      break;
    case CreateSourceFileUploadActionType.UPDATE_FILE_PROGRESS:
      newState = updateFileProgress(state, action);
      break;
    case CreateSourceFileUploadActionType.SET_OVERALL_PROGRESS:
      newState = setOverallProgress(state, action);
      break;
    case CreateSourceFileUploadActionType.SET_SHOULD_SHOW_ERRORS:
      newState = setShouldShowErrors(state, action);
      break;
  }

  return {
    ...newState,
    isUploading: isFileUploadInProgress(newState.phase),
  };
};

export default createSourceFileUploadReducer;
